import React, {
  createContext, useContext, useEffect, useMemo, useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  Article, ArticleResponse, Meta, useArticlesGet,
  useArticleTagsGet,
} from '../../../hooks/api/article';
import {
  Category, SubCategory, useCategoryGet, useSubCategoryGet,
} from '../../../hooks/api/category';
import { SortType } from '../../Pages/SearchResults';

interface ArticleContextProps {
  data?: Article[] | null;
  meta?: Meta | null;
  loading: boolean;
  error: any;
  search?: string;
  filters?: ArticleFilters;
  categories?: Category[];
  subCategories?: SubCategory[];
  categoryLoading?: boolean;
  tags?: string[];
  setFilters?: (filters: ArticleFilters) => void;
  fetch?: (params: any) => Promise<ArticleResponse | null>;
  setData?: (data: Article[] | null) => void;
  setSearch?: (text: string) => void;
  buildQueryString?: (titleOverride?: string, customFilters?: ArticleFilters) => string;
  currentProduct?: { id: string; name: string } | null;
  setCurrentProduct?: (product: { id: string; name: string } | null) => void;
  sortValue?: SortType;
  setSortValue?: (value: SortType) => void;

}
interface ArticleFilters {
  categoryId?: string[];
  subCategoryId?: string[];
  types?: string[];
  tags?: string[];
}

const ArticleContext = createContext<ArticleContextProps>({
  data: null,
  loading: false,
  error: null,

});

// eslint-disable-next-line react/function-component-definition
export const ArticleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    data: response,
    loading,
    error,
    fetch: fetchArticles,
  } = useArticlesGet();
  const { data: subCategories = [], loading: loadingSubCategories } = useSubCategoryGet();
  const [currentProduct, setCurrentProduct] = useState<{ id: string; name: string } | null>(null);
  const [sortValue, setSortValue] = useState<SortType>(SortType.MostViewed);
  const categoryGet = useCategoryGet({ autoStart: false });
  const {
    data: categories = [],
    loading: loadingCategories,
  } = categoryGet;
  const [data, setData] = useState<Article[] | null>(null);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [search, setSearch] = useState<string>('');
  const { data: tags = [] } = useArticleTagsGet();
  const [filters, setFilters] = useState<ArticleFilters>({
    categoryId: [],
    subCategoryId: [],
    types: [],
  });
  const [wasFetchedOnce, setWasFetchedOnce] = useState(false);

  useEffect(() => {
    if (currentProduct) {
      categoryGet.fetch({ productId: currentProduct.id });
    } else {
      categoryGet.fetch();
    }
  }, [currentProduct]);
  // const data = response?.data ?? null;

  const fetch = async (params: any, persist = true) => {
    const res = await fetchArticles(params);

    if (persist && res) {
      setData(res.data ?? null);
      setMeta(res.meta ?? null);
      setWasFetchedOnce(true);
    }

    return res;
  };
  const buildQueryString = (
    titleOverride?: string,
    customFilters?: ArticleFilters,
  ): string => {
    const params = new URLSearchParams();
    const title = titleOverride ?? search;
    const f = customFilters ?? filters;

    if (title) params.set('title', title);
    if (f?.categoryId?.length) params.set('categoryId', f.categoryId.join(','));
    if (f?.subCategoryId?.length) params.set('subCategoryId', f.subCategoryId.join(','));
    if (f?.types?.length) params.set('type', f.types.join(','));
    if (f?.tags?.length) params.set('tags', f.tags.join(','));
    params.set('page', '1');

    return params.toString();
  };

  const value = useMemo(() => ({
    data,
    meta,
    loading,
    error,
    search,
    filters,
    setFilters,
    fetch,
    setData,
    setSearch,
    categories,
    subCategories,
    categoryLoading: loadingCategories || loadingSubCategories,
    tags,
    currentProduct,
    setCurrentProduct,
    buildQueryString,
    sortValue,
    setSortValue,
    wasFetchedOnce,
  }), [data, meta, loading, error, search, fetch, setSearch, setFilters, filters, categories, subCategories,
    currentProduct,
    sortValue,
    setSortValue,
    setCurrentProduct]);

  return (
    <ArticleContext.Provider value={value}>
      {children}
    </ArticleContext.Provider>
  );
};

export const useArticleContext = () => useContext(ArticleContext);
